import { useDispatch, useSelector } from 'react-redux';

import { toast } from 'sonner';
import { BaseButton, ConfirmationModal } from 'wbs-components-lib';

import { Box, Typography } from '@mui/material';

import { BetEventModel, BetOutcomeModel } from '@shared/types';

import { useDeleteBetEventMutation, useDeleteBetEventOutcomeMutation, useUnfinishBetEventMutation } from '@services';

import {
  AppDispatch,
  deleteBetEvent,
  deleteBetOutcome,
  getIsBetEventCreateModalOpenedSelector,
  getIsBetEventDeleteModalOpenedSelector,
  getIsBetEventEditModalOpenedSelector,
  getIsBetEventFinishModalOpenedSelector,
  getIsBetEventUnfinishModalOpenedSelector,
  getIsBetOutcomeCreateModalOpenedSelector,
  getIsBetOutcomeDeleteModalOpenedSelector,
  getIsBetOutcomeEditModalOpenedSelector,
  getIsExportWinStreaksModalOpenedSelector,
  getProcessingBetEventSelector,
  getProcessingBetOutcomeSelector,
  setBetOutcomes,
  setIsBetEventCreateModalOpened,
  setIsBetEventDeleteModalOpened,
  setIsBetEventEditModalOpened,
  setIsBetEventFinishModalOpened,
  setIsBetEventUnfinishModalOpened,
  setIsBetOutcomeCreateModalOpened,
  setIsBetOutcomeDeleteModalOpened,
  setIsBetOutcomeEditModalOpened,
  setIsExportWinStreaksModalOpened,
  setProcessingBetEvent,
  setProcessingBetOutcome,
  updateBetEvent,
} from '@store';

import { BetSystemEventsList } from './BetSystemEventsList';
import {
  BetEventCreateModal,
  BetEventEditModal,
  BetEventFinishModal,
  BetOutcomeCreateModal,
  BetOutcomeEditModal,
  UserWinStreaksExportModal,
} from './BetSystemSettingsModals';

import styles from './BetSystemSettingsPage.module.css';

import { PageLayout } from 'v2/components/pageLayout/PageLayout';

const { betSystemSettingsHeader, betSystemHeaderButtonSettings } = styles;

export const BetSystemSettingsPage = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [deleteBetEventById, { isLoading: isBetEventDeleting }] = useDeleteBetEventMutation();
  const [unfinishBetEventById, { isLoading: isBetEventUnfinishing }] = useUnfinishBetEventMutation();
  const [deleteBetEventOutcomeById, { isLoading: isBetOutcomeDeleting }] = useDeleteBetEventOutcomeMutation();

  const processingBetEvent = useSelector(getProcessingBetEventSelector);
  const processingBetOutcome = useSelector(getProcessingBetOutcomeSelector);

  const isBetEventCreateModalOpened = useSelector(getIsBetEventCreateModalOpenedSelector);
  const isBetEventDeleteModalOpened = useSelector(getIsBetEventDeleteModalOpenedSelector);
  const isBetEventEditModalOpened = useSelector(getIsBetEventEditModalOpenedSelector);
  const isBetEventFinishModalOpened = useSelector(getIsBetEventFinishModalOpenedSelector);
  const isBetEventUnfinishModalOpened = useSelector(getIsBetEventUnfinishModalOpenedSelector);
  const isBetOutcomeCreateModalOpened = useSelector(getIsBetOutcomeCreateModalOpenedSelector);
  const isBetOutcomeDeleteModalOpened = useSelector(getIsBetOutcomeDeleteModalOpenedSelector);
  const isBetOutcomeEditModalOpened = useSelector(getIsBetOutcomeEditModalOpenedSelector);
  const isExportWinStreaksModalOpened = useSelector(getIsExportWinStreaksModalOpenedSelector);

  const handleUnfinishBetEvent = async () => {
    const { id: betEventId } = processingBetEvent;

    const { betEvent, betEventOutcomes } = await unfinishBetEventById(betEventId).unwrap();

    dispatch(updateBetEvent(betEvent));

    dispatch(setBetOutcomes({ betEventId, betEventOutcomes }));

    handleBetEventUnfinishModalClose();

    toast.success('Success: Bet event has been unfinished!');
  };

  const handleBetEventDelete = async () => {
    const deletedBetEvent = await deleteBetEventById(processingBetEvent.id).unwrap();

    dispatch(deleteBetEvent(deletedBetEvent.id));

    toast.success('Success: Bet event has been deleted!');

    handleBetEventDeleteModalClose();
  };

  const handleBetOutcomeDelete = async () => {
    const { eventId: betEventId, id: betOutcomeId } = processingBetOutcome;

    await deleteBetEventOutcomeById({ betEventId, betOutcomeId }).unwrap();

    dispatch(
      deleteBetOutcome({
        betOutcomeId,
        betEventId,
      })
    );

    toast.success('Success: Bet outcome has been deleted!');

    handleBetOutcomeDeleteModalClose();
  };

  const handleBetEventCreateModalClose = () => {
    dispatch(setIsBetEventCreateModalOpened(false));
  };

  const handleBetEventEditModalClose = () => {
    dispatch(setProcessingBetEvent({} as BetEventModel));

    dispatch(setIsBetEventEditModalOpened(false));
  };

  const handleBetOutcomeCreateModalClose = () => {
    dispatch(setIsBetOutcomeCreateModalOpened(false));
  };

  const handleBetOutcomeEditModalClose = () => {
    dispatch(setProcessingBetOutcome({} as BetOutcomeModel));

    dispatch(setIsBetOutcomeEditModalOpened(false));
  };

  const handleBetEventDeleteModalClose = () => {
    dispatch(setProcessingBetEvent({} as BetEventModel));

    dispatch(setIsBetEventDeleteModalOpened(false));
  };

  const handleBetEventFinishModalClose = () => {
    dispatch(setProcessingBetEvent({} as BetEventModel));

    dispatch(setIsBetEventFinishModalOpened(false));
  };

  const handleBetEventUnfinishModalClose = () => {
    dispatch(setProcessingBetEvent({} as BetEventModel));

    dispatch(setIsBetEventUnfinishModalOpened(false));
  };

  const handleBetOutcomeDeleteModalClose = () => {
    dispatch(setProcessingBetOutcome({} as BetOutcomeModel));

    dispatch(setIsBetOutcomeDeleteModalOpened(false));
  };

  const handleBetEventCreateModalOpen = () => {
    dispatch(setIsBetEventCreateModalOpened(true));
  };

  const handleExportWinStreaksModalOpen = () => {
    dispatch(setIsExportWinStreaksModalOpened(true));
  };

  const handleExportWinStreaksModalClose = () => {
    dispatch(setIsExportWinStreaksModalOpened(false));
  };

  return (
    <>
      <PageLayout>
        <Box className={betSystemSettingsHeader}>
          <Typography margin="10px" fontSize="24px" fontWeight={600}>
            Bet System Settings
          </Typography>
          <Box className={betSystemHeaderButtonSettings}>
            <BaseButton onClick={handleExportWinStreaksModalOpen}>Export win streaks</BaseButton>
            <BaseButton onClick={handleBetEventCreateModalOpen}>Create Event</BaseButton>
          </Box>
        </Box>
        <BetSystemEventsList />
      </PageLayout>
      <BetEventCreateModal isOpened={isBetEventCreateModalOpened} handleClose={handleBetEventCreateModalClose} />
      <BetEventEditModal
        betEvent={processingBetEvent}
        isOpened={isBetEventEditModalOpened}
        handleClose={handleBetEventEditModalClose}
      />
      <BetEventFinishModal
        betEvent={processingBetEvent}
        isOpened={isBetEventFinishModalOpened}
        handleClose={handleBetEventFinishModalClose}
      />
      <BetOutcomeCreateModal
        betEventId={processingBetEvent.id}
        handleClose={handleBetOutcomeCreateModalClose}
        isOpened={isBetOutcomeCreateModalOpened}
      />
      <BetOutcomeEditModal
        betOutcome={processingBetOutcome}
        isOpened={isBetOutcomeEditModalOpened}
        handleClose={handleBetOutcomeEditModalClose}
      />
      <UserWinStreaksExportModal
        isOpened={isExportWinStreaksModalOpened}
        handleClose={handleExportWinStreaksModalClose}
      />
      <ConfirmationModal
        buttonConfirmedText="Unfinish"
        headerTitle="Unfinish Bet Event"
        onSubmit={handleUnfinishBetEvent}
        isOpened={isBetEventUnfinishModalOpened}
        isLoading={isBetEventUnfinishing}
        handleClose={handleBetEventUnfinishModalClose}
      >
        Are you sure that you want to unfinish this bet event?
      </ConfirmationModal>
      <ConfirmationModal
        buttonConfirmedText="Delete"
        headerTitle="Delete Bet Event"
        onSubmit={handleBetEventDelete}
        isOpened={isBetEventDeleteModalOpened}
        isLoading={isBetEventDeleting}
        handleClose={handleBetEventDeleteModalClose}
      >
        Are you sure that you want to delete this bet event?
      </ConfirmationModal>
      <ConfirmationModal
        buttonConfirmedText="Delete"
        headerTitle="Delete Bet Outcome"
        onSubmit={handleBetOutcomeDelete}
        isOpened={isBetOutcomeDeleteModalOpened}
        isLoading={isBetOutcomeDeleting}
        handleClose={handleBetOutcomeDeleteModalClose}
      >
        Are you sure that you want to delete this bet outcome?
      </ConfirmationModal>
    </>
  );
};
